/* ============================================================
   Nuzzle custom product page — behaviour (vanilla JS, no deps).
   Scoped to [data-nz-*] hooks only. Manages presentation state
   (bundle tiers with per-unit design selectors, add-ons, totals,
   gallery, sticky bar, accordions, announcement rotation, demo
   videos) and owns add-to-cart: the buy box posts every selected
   line in ONE /cart/add.js call, then sends the shopper to the
   cart page.
   ============================================================ */
(function () {
  'use strict';

  var heroState = null; // shared with sticky bar

  function formatMoney(cents, currency, trimZeros) {
    try {
      var opts = { style: 'currency', currency: currency || 'USD' };
      /* A flat saving reads better as "$20" than "$20.00". */
      if (trimZeros && cents % 100 === 0) {
        opts.minimumFractionDigits = 0;
        opts.maximumFractionDigits = 0;
      }
      return new Intl.NumberFormat(document.documentElement.lang || undefined, opts).format(cents / 100);
    } catch (e) {
      if (trimZeros && cents % 100 === 0) return (cents / 100).toFixed(0) + ' ' + (currency || '');
      return (cents / 100).toFixed(2) + ' ' + (currency || '');
    }
  }

  /* ---------------- Hero ---------------- */
  function initHero(root) {
    if (!root || root.dataset.nzInit) return;
    root.dataset.nzInit = 'true';

    var cfgEl = root.querySelector('[data-nz-config]');
    if (!cfgEl) return;
    var cfg;
    try {
      cfg = JSON.parse(cfgEl.textContent);
    } catch (e) {
      return;
    }

    var variants = cfg.variants || [];
    var currency = cfg.currency;
    var form = root.querySelector('form[data-type="add-to-cart-form"]');
    var idInput = form ? form.querySelector('input[name="id"]') : null;
    var qtyInput = form ? form.querySelector('input[name="quantity"]') : null;
    var atcBtn = root.querySelector('[ref="addToCartButton"]');
    var atcLabel = root.querySelector('[data-nz-atc-label]');
    var tiers = Array.prototype.slice.call(root.querySelectorAll('[data-nz-tier]'));

    /* Some products have a real second axis (plug type, size). When they do,
       a variant is only identified by BOTH values — matching on the first
       alone would quietly pick whichever plug happens to come first. */
    var hasOption2 = variants.some(function (v) { return v.option2 != null; });

    function variantByOption(value, value2) {
      return variants.find(function (v) {
        if (v.option !== value) return false;
        if (!hasOption2 || value2 == null) return true;
        return v.option2 === value2;
      });
    }

    /* Best available match: keep the chosen second value if that combination
       exists, otherwise fall back to any variant with the chosen first value. */
    function resolveVariant(value, value2) {
      return variantByOption(value, value2) || variantByOption(value);
    }

    var initialVariant =
      variants.find(function (v) { return String(v.id) === String(idInput && idInput.value); }) || variants[0];

    var state = {
      tierIndex: 0,
      tierQty: 1,
      tierDiscount: 0,
      unitValues: [initialVariant ? initialVariant.option : ''],
      unitValues2: [initialVariant ? initialVariant.option2 : null],
      addonTotal: 0,
    };

    tiers.forEach(function (tier, i) {
      if (tier.getAttribute('aria-pressed') === 'true') {
        state.tierIndex = i;
        state.tierQty = parseInt(tier.dataset.nzTierQty, 10) || 1;
        state.tierDiscount = parseFloat(tier.dataset.nzTierDiscount) || 0;
      }
    });
    state.unitValues = [];
    state.unitValues2 = [];
    for (var u = 0; u < state.tierQty; u++) {
      state.unitValues.push(initialVariant ? initialVariant.option : '');
      state.unitValues2.push(initialVariant ? initialVariant.option2 : null);
    }

    function unitVariant(i) {
      return resolveVariant(state.unitValues[i], state.unitValues2[i]);
    }

    function primaryVariant() {
      return unitVariant(0) || initialVariant;
    }

    function computeUnitsTotal(discount) {
      var sum = 0;
      state.unitValues.forEach(function (val, i) {
        var v = unitVariant(i);
        if (v) sum += v.price;
      });
      return Math.round(sum * (1 - discount / 100));
    }

    function computeTotal() {
      return computeUnitsTotal(state.tierDiscount) + state.addonTotal;
    }

    function recalcAddons() {
      var total = 0;
      root.querySelectorAll('.nz-addon').forEach(function (row) {
        if (row.dataset.nzAddonSoldout === 'true') return;
        var on = row.dataset.on === 'true';
        var price = parseInt(row.dataset.nzAddonPrice, 10) || 0;
        if (on && price > 0) total += price;
      });
      state.addonTotal = total;
    }

    /* Switching an add-on's variant changes its price. Repaint that row and the
       running total straight away, so the number on the button is always the
       number the shopper is about to be charged. */
    root.addEventListener('nz:addon-variant', function (e) {
      var row = e.target && e.target.closest ? e.target.closest('.nz-addon') : null;
      if (!row) return;

      var price = parseInt(row.dataset.nzAddonPrice, 10) || 0;
      var priceLabel = row.querySelector('[data-nz-addon-price-label]');
      if (priceLabel) priceLabel.textContent = formatMoney(price, currency);

      var sel = row.querySelector('[data-nz-addon-variant-select]');
      var opt = sel ? sel.options[sel.selectedIndex] : null;
      var compare = opt ? parseInt(opt.getAttribute('data-compare'), 10) || 0 : 0;
      var compareLabel = row.querySelector('[data-nz-addon-compare-label]');
      if (compareLabel) {
        var showCompare = compare > price;
        compareLabel.hidden = !showCompare;
        compareLabel.textContent = showCompare ? formatMoney(compare, currency) : '';
      }

      recalcAddons();
      renderTotal();
      emit();
    });

    function renderTierRows() {
      var pv = primaryVariant();
      if (!pv) return;
      tiers.forEach(function (tier, i) {
        var q = parseInt(tier.dataset.nzTierQty, 10) || 1;
        var disc = parseFloat(tier.dataset.nzTierDiscount) || 0;
        var base, cmpBase;
        if (i === state.tierIndex) {
          base = 0;
          cmpBase = 0;
          state.unitValues.forEach(function (val, u2) {
            var v = unitVariant(u2);
            if (!v) return;
            base += v.price;
            cmpBase += v.compare_at_price && v.compare_at_price > v.price ? v.compare_at_price : v.price;
          });
        } else {
          base = pv.price * q;
          cmpBase = (pv.compare_at_price && pv.compare_at_price > pv.price ? pv.compare_at_price : pv.price) * q;
        }
        var price = Math.round(base * (1 - disc / 100));
        var compare = 0;
        if (disc > 0) compare = base;
        if (cmpBase > base && cmpBase > compare) compare = cmpBase;

        var priceEl = tier.querySelector('[data-nz-tier-price]');
        var compareEl = tier.querySelector('[data-nz-tier-compare]');
        var saveEl = tier.querySelector('[data-nz-tier-save]');
        if (priceEl) priceEl.textContent = formatMoney(price, currency);
        if (compareEl) {
          var showCmp = compare > price;
          compareEl.hidden = !showCmp;
          compareEl.textContent = showCmp ? formatMoney(compare, currency) : '';
        }
        if (saveEl) {
          var highlight = tier.dataset.nzTierHighlight || '';
          if (compare > price) {
            var text = saveEl.dataset.nzSaveTemplate.replace('[amount]', formatMoney(compare - price, currency));
            saveEl.textContent = highlight ? text + ' ' + highlight : text;
            saveEl.hidden = false;
          } else {
            saveEl.textContent = highlight;
            saveEl.hidden = !highlight;
          }
        }
      });
    }

    function renderPrice() {
      var v = primaryVariant();
      if (!v) return;
      var priceEl = root.querySelector('[data-nz-price]');
      var compareEl = root.querySelector('[data-nz-compare]');
      var badgeEl = root.querySelector('[data-nz-save-badge]');
      if (priceEl) priceEl.textContent = formatMoney(v.price, currency);
      var showCmp = v.compare_at_price && v.compare_at_price > v.price;
      if (compareEl) {
        compareEl.hidden = !showCmp;
        if (showCmp) compareEl.textContent = formatMoney(v.compare_at_price, currency);
      }
      if (badgeEl) {
        if (showCmp) {
          /* Floor, to match the integer division Liquid does server-side —
             otherwise the badge would jump a point when a variant changes. */
          var pct = Math.floor(((v.compare_at_price - v.price) / v.compare_at_price) * 100);
          var amount = v.compare_at_price - v.price;
          badgeEl.textContent = (badgeEl.dataset.nzSaveBadgeTemplate || 'SAVE [percent]%')
            .replace('[percent]', pct)
            .replace('[amount]', formatMoney(amount, currency, true));
          badgeEl.hidden = false;
        } else {
          badgeEl.hidden = true;
        }
      }
    }

    function renderTotal() {
      var totalEl = root.querySelector('[data-nz-total]');
      if (totalEl) totalEl.textContent = formatMoney(computeTotal(), currency);
    }

    function renderAvailability() {
      var v = primaryVariant();
      var available = !!(v && v.available);
      if (atcBtn) atcBtn.disabled = !available;
      if (atcLabel) {
        atcLabel.textContent = available ? atcLabel.dataset.nzTextAdd : atcLabel.dataset.nzTextSoldout;
      }
    }

    function renderDesignName() {
      var v = primaryVariant();
      root.querySelectorAll('[data-nz-design-name]').forEach(function (el) {
        if (v) el.textContent = v.option;
      });
    }

    function selectMedia(index) {
      root.querySelectorAll('[data-nz-main-media]').forEach(function (img) {
        img.hidden = String(img.dataset.nzMainMedia) !== String(index);
      });
      root.querySelectorAll('.nz-thumb').forEach(function (t) {
        t.setAttribute('aria-current', String(t.dataset.nzThumb) === String(index) ? 'true' : 'false');
      });
    }

    function syncFormInputs() {
      /* form always carries unit #1's variant + how many units share it */
      var pv = primaryVariant();
      if (!pv) return;
      var count = 0;
      for (var i = 0; i < state.unitValues.length; i++) {
        var v = unitVariant(i);
        if (v && v.id === pv.id) count++;
      }
      if (idInput) idInput.value = pv.id;
      if (qtyInput) qtyInput.value = count || 1;
    }

    /* Grey out second-axis values that do not exist for the design chosen on
       the same row, and snap the row to a combination that does exist. */
    function renderOption2() {
      if (!hasOption2) return;
      root.querySelectorAll('select[data-nz-unit2]').forEach(function (sel) {
        var i = parseInt(sel.dataset.nzUnit2, 10) || 0;
        var first = state.unitValues[i];
        var anyEnabled = null;
        for (var o = 0; o < sel.options.length; o++) {
          var opt = sel.options[o];
          var match = variantByOption(first, opt.value);
          var ok = !!(match && match.available);
          opt.disabled = !ok;
          if (ok && anyEnabled === null) anyEnabled = opt.value;
        }
        var current = state.unitValues2[i];
        var currentMatch = variantByOption(first, current);
        if ((!currentMatch || !currentMatch.available) && anyEnabled !== null) {
          state.unitValues2[i] = anyEnabled;
          current = anyEnabled;
        }
        if (current != null) sel.value = current;
      });
    }

    function emit() {
      var pv = primaryVariant();
      heroState = {
        designName: pv ? pv.option : '',
        units: state.tierQty,
        total: formatMoney(computeTotal(), currency),
        available: !!(pv && pv.available),
      };
      document.dispatchEvent(new CustomEvent('nz:state', { detail: heroState }));
    }

    function renderAll() {
      renderOption2();
      renderPrice();
      renderTierRows();
      renderTotal();
      renderAvailability();
      renderDesignName();
      syncFormInputs();
      emit();
    }

    /* tier selection */
    tiers.forEach(function (tier, i) {
      var head = tier.querySelector('[data-nz-tier-select]');
      if (!head) return;
      head.addEventListener('click', function () {
        tiers.forEach(function (t, j) {
          t.setAttribute('aria-pressed', j === i ? 'true' : 'false');
          var units = t.querySelector('[data-nz-units]');
          if (units) units.hidden = j !== i;
        });
        state.tierIndex = i;
        state.tierQty = parseInt(tier.dataset.nzTierQty, 10) || 1;
        state.tierDiscount = parseFloat(tier.dataset.nzTierDiscount) || 0;

        /* resize unit values, carrying over previous choices */
        var next = [];
        var next2 = [];
        var selects = tier.querySelectorAll('select[data-nz-unit]');
        var selects2 = tier.querySelectorAll('select[data-nz-unit2]');
        for (var u = 0; u < state.tierQty; u++) {
          var carried = state.unitValues[u] || state.unitValues[state.unitValues.length - 1] || '';
          next.push(carried);
          if (selects[u] && carried) selects[u].value = carried;

          var carried2 =
            state.unitValues2[u] != null
              ? state.unitValues2[u]
              : state.unitValues2[state.unitValues2.length - 1];
          next2.push(carried2 == null ? null : carried2);
          if (selects2[u] && carried2 != null) selects2[u].value = carried2;
        }
        state.unitValues = next;
        state.unitValues2 = next2;

        var pv = primaryVariant();
        if (pv && pv.media_position) selectMedia(pv.media_position - 1);
        renderAll();
      });
    });

    /* Reflect the chosen unit #1 in the gallery and the ?variant= in the URL,
       so a shared link reopens on exactly the combination that was picked. */
    function afterUnitChange(unitIndex) {
      renderOption2();
      if (unitIndex !== 0) return;
      var v = unitVariant(0);
      if (!v) return;
      if (v.media_position) selectMedia(v.media_position - 1);
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', url);
      } catch (e) { /* noop */ }
    }

    /* per-unit design selectors */
    tiers.forEach(function (tier, i) {
      tier.querySelectorAll('select[data-nz-unit]').forEach(function (sel) {
        sel.addEventListener('change', function () {
          if (i !== state.tierIndex) return;
          var unitIndex = parseInt(sel.dataset.nzUnit, 10) || 0;
          state.unitValues[unitIndex] = sel.value;
          afterUnitChange(unitIndex);
          renderAll();
        });
      });

      /* per-unit second-axis selectors (plug type, size…) */
      tier.querySelectorAll('select[data-nz-unit2]').forEach(function (sel) {
        sel.addEventListener('change', function () {
          if (i !== state.tierIndex) return;
          var unitIndex = parseInt(sel.dataset.nzUnit2, 10) || 0;
          state.unitValues2[unitIndex] = sel.value;
          afterUnitChange(unitIndex);
          renderAll();
        });
      });
    });

    /* thumbnails */
    root.querySelectorAll('.nz-thumb').forEach(function (btn) {
      btn.addEventListener('click', function () {
        selectMedia(btn.dataset.nzThumb);
      });
    });

    /* add-on toggles */
    root.querySelectorAll('.nz-addon').forEach(function (row) {
      var toggle = row.querySelector('.nz-toggle');
      var propInput = row.querySelector('input[data-nz-addon-prop]');
      if (!toggle || row.dataset.nzAddonSoldout === 'true') return;
      toggle.addEventListener('click', function () {
        var on = row.dataset.on !== 'true';
        row.dataset.on = on ? 'true' : 'false';
        toggle.setAttribute('aria-pressed', on ? 'true' : 'false');
        if (propInput) propInput.disabled = !on;
        clearAtcError();
        recalcAddons();
        renderTotal();
        emit();
      });
    });

    /* ---- Add-to-cart error surface ---- */
    var atcErrorBox = root.querySelector('[data-nz-atc-error]');
    var atcErrorText = root.querySelector('[data-nz-atc-error-text]');

    function clearAtcError() {
      if (atcErrorBox) atcErrorBox.hidden = true;
    }

    function showAtcError(message) {
      if (!atcErrorBox) {
        window.alert(message);
        return;
      }
      if (atcErrorText) atcErrorText.textContent = message;
      atcErrorBox.hidden = false;
      try {
        atcErrorBox.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      } catch (e) { /* noop */ }
    }

    /* Add to cart — ONE atomic request carrying every selected bundle unit and
       every switched-on add-on, then straight to the cart page. Doing it in a
       single call is what keeps the cart from ever disagreeing with what the
       buy box showed: no half-added order, no race between two requests. */
    if (form) {
      var submitting = false;
      var addButton = form.querySelector('button[name="add"]');

      function setAtcBusy(on) {
        if (addButton) addButton.disabled = on;
        if (atcLabel && on) atcLabel.textContent = atcLabel.dataset.nzTextAdding || 'ADDING…';
      }

      function restoreAtcLabel() {
        var v = primaryVariant();
        if (!atcLabel) return;
        atcLabel.textContent =
          v && v.available ? atcLabel.dataset.nzTextAdd : atcLabel.dataset.nzTextSoldout;
      }

      form.addEventListener('submit', function (e) {
        e.preventDefault();
        e.stopImmediatePropagation();
        if (submitting) return;

        syncFormInputs();

        var pv = primaryVariant();
        if (!pv || !pv.available) return;

        /* Bundle units, grouped so three of the same design are one line of 3.
           Each unit resolves through BOTH selectors, so the plug type the
           shopper picked is the one that reaches the cart. */
        var groups = {};
        state.unitValues.forEach(function (val, i) {
          var v = unitVariant(i) || pv;
          groups[v.id] = (groups[v.id] || 0) + 1;
        });
        if (!Object.keys(groups).length) groups[pv.id] = 1;

        var items = Object.keys(groups).map(function (id) {
          return { id: parseInt(id, 10), quantity: groups[id] };
        });

        /* Paid add-ons become their own line; free ones ride along as a
           line-item property on the main product. */
        var props = {};
        root.querySelectorAll('.nz-addon[data-on="true"]').forEach(function (row) {
          if (row.dataset.nzAddonSoldout === 'true') return;
          var vid = row.dataset.nzAddonVariant;
          if (vid) {
            items.push({ id: parseInt(vid, 10), quantity: 1 });
            return;
          }
          var propInput = row.querySelector('input[data-nz-addon-prop]');
          if (propInput && propInput.name) {
            var match = propInput.name.match(/^properties\[(.+)\]$/);
            if (match) props[match[1]] = propInput.value || 'Yes';
          }
        });
        if (Object.keys(props).length) items[0].properties = props;

        submitting = true;
        clearAtcError();
        setAtcBusy(true);

        var routes = (window.Theme && window.Theme.routes) || {};
        fetch(routes.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items }),
        })
          .then(function (res) {
            return res.json().then(
              function (body) { return { ok: res.ok, body: body }; },
              function () { return { ok: res.ok, body: null }; }
            );
          })
          .then(function (result) {
            if (!result.ok) {
              var body = result.body || {};
              var err = new Error(body.description || body.message || '');
              err.shopify = true;
              throw err;
            }
            window.location.href = routes.cart_url || '/cart';
          })
          .catch(function (err) {
            /* Deliberately NOT falling back to a plain form post here. That would
               add the projector on its own and drop the add-ons the shopper had
               switched on, without telling them — they would only find out after
               paying. Better to say what happened and let them retry. */
            submitting = false;
            setAtcBusy(false);
            restoreAtcLabel();
            showAtcError(
              (err && err.shopify && err.message) ||
                'We could not add that to your cart just now. Please check your selection and try again.'
            );
          });
      }, true);
    }

    /* product details accordion */
    root.querySelectorAll('[data-nz-accordion] .nz-acc__toggle').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var panel = btn.nextElementSibling;
        var open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        if (panel) panel.style.maxHeight = open ? '0px' : panel.scrollHeight + 'px';
      });
    });

    recalcAddons();
    renderAll();
  }

  /* ---------------- Sticky mobile ATC ---------------- */
  function initSticky(root) {
    if (!root || root.dataset.nzInit) return;
    root.dataset.nzInit = 'true';

    var hero = document.querySelector('[data-nz-hero]');
    if (!hero) return;

    var btn = root.querySelector('[data-nz-sticky-atc]');
    var meta = root.querySelector('[data-nz-sticky-meta]');
    var total = root.querySelector('[data-nz-sticky-total]');

    root.classList.add('nz-sticky--enabled');

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var pastHero = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
            root.classList.toggle('nz-sticky--visible', pastHero);
          });
        },
        { rootMargin: '80px 0px 0px 0px' }
      );
      observer.observe(hero);
    }

    function renderState(s) {
      if (!s) return;
      if (meta) {
        var parts = [];
        if (meta.dataset.nzShowDesign === 'true' && s.designName) parts.push(s.designName);
        if (meta.dataset.nzShowQty === 'true') parts.push(s.units + '×');
        meta.textContent = parts.join(' · ');
      }
      if (total) {
        total.textContent = total.dataset.nzShowTotal === 'true' ? s.total : '';
      }
      if (btn) btn.disabled = !s.available;
    }

    document.addEventListener('nz:state', function (e) { renderState(e.detail); });
    renderState(heroState);

    if (btn) {
      btn.addEventListener('click', function () {
        var heroBtn = hero.querySelector('[ref="addToCartButton"]');
        if (heroBtn && !heroBtn.disabled) heroBtn.click();
      });
    }
  }

  /* ---------------- Announcement rotation ---------------- */
  function initAnnouncement(root) {
    if (!root || root.dataset.nzInit) return;
    root.dataset.nzInit = 'true';

    var msgs = Array.prototype.slice.call(root.querySelectorAll('.nz-announcement__msg'));
    if (msgs.length < 2) return;
    var interval = (parseFloat(root.dataset.nzSpeed) || 3.5) * 1000;
    var index = 0;
    msgs.forEach(function (m, i) { m.hidden = i !== 0; });
    setInterval(function () {
      msgs[index].hidden = true;
      index = (index + 1) % msgs.length;
      var el = msgs[index];
      el.hidden = false;
      /* restart the fade animation */
      el.style.animation = 'none';
      void el.offsetWidth;
      el.style.animation = '';
    }, interval);
  }

  /* ---------------- FAQ accordion ---------------- */
  function initFaq(root) {
    if (!root || root.dataset.nzInit) return;
    root.dataset.nzInit = 'true';

    var buttons = root.querySelectorAll('.nz-faq__q');
    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') === 'true';
        if (root.dataset.nzSingleOpen === 'true') {
          buttons.forEach(function (other) {
            if (other !== btn) {
              other.setAttribute('aria-expanded', 'false');
              var p = document.getElementById(other.getAttribute('aria-controls'));
              if (p) p.style.maxHeight = '0px';
            }
          });
        }
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        var panel = document.getElementById(btn.getAttribute('aria-controls'));
        if (panel) panel.style.maxHeight = open ? '0px' : panel.scrollHeight + 'px';
      });
    });

    /* open the first item by default if requested */
    if (root.dataset.nzOpenFirst === 'true' && buttons.length) {
      var first = buttons[0];
      first.setAttribute('aria-expanded', 'true');
      var panel = document.getElementById(first.getAttribute('aria-controls'));
      if (panel) panel.style.maxHeight = panel.scrollHeight + 'px';
    }
  }

  /* ---------------- Demos: data-conscious video playback ---------------- */
  function initDemos(root) {
    if (!root || root.dataset.nzInit) return;
    root.dataset.nzInit = 'true';

    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var videos = root.querySelectorAll('video');
    if (!videos.length) return;

    if (reduced || !('IntersectionObserver' in window)) {
      videos.forEach(function (v) { v.setAttribute('controls', ''); });
      return;
    }
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target;
          if (entry.isIntersecting) {
            video.play().catch(function () { video.setAttribute('controls', ''); });
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.35 }
    );
    videos.forEach(function (v) { observer.observe(v); });
  }

  /* ---------------- boot ---------------- */
  function initAll() {
    document.querySelectorAll('[data-nz-hero]').forEach(initHero);
    document.querySelectorAll('[data-nz-sticky]').forEach(initSticky);
    document.querySelectorAll('[data-nz-announcement]').forEach(initAnnouncement);
    document.querySelectorAll('[data-nz-faq]').forEach(initFaq);
    document.querySelectorAll('[data-nz-demos]').forEach(initDemos);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  /* re-init inside the theme editor when a section reloads */
  document.addEventListener('shopify:section:load', initAll);
})();